import { ChangeEvent, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faStar } from '@fortawesome/pro-solid-svg-icons/faStar';
import { faPenToSquare } from '@fortawesome/pro-solid-svg-icons/faPenToSquare';
import { faCreditCard } from '@fortawesome/pro-solid-svg-icons/faCreditCard';
import { faShieldCheck } from '@fortawesome/pro-solid-svg-icons/faShieldCheck';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faEye } from '@fortawesome/pro-solid-svg-icons/faEye';
import { faEyeSlash } from '@fortawesome/pro-solid-svg-icons/faEyeSlash';
import { faFlag } from '@fortawesome/pro-solid-svg-icons/faFlag';
import { faComment } from '@fortawesome/pro-solid-svg-icons/faComment';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons/faCalendar';
import { faSun } from '@fortawesome/pro-solid-svg-icons/faSun';
import { faMoon } from '@fortawesome/pro-solid-svg-icons/faMoon';
import { faSquare } from '@fortawesome/pro-regular-svg-icons/faSquare';
import { faSyringe } from '@fortawesome/pro-solid-svg-icons/faSyringe';
import { faHeart } from '@fortawesome/pro-solid-svg-icons/faHeart';
import { faHeart as faHeartRegular } from '@fortawesome/pro-regular-svg-icons/faHeart';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { faHeartPulse } from '@fortawesome/pro-solid-svg-icons/faHeartPulse';
import { faUsers } from '@fortawesome/pro-solid-svg-icons/faUsers';
import { faCommentDots } from '@fortawesome/pro-solid-svg-icons/faCommentDots';
import { faHandshake } from '@fortawesome/pro-solid-svg-icons/faHandshake';
import { faClock } from '@fortawesome/pro-solid-svg-icons/faClock';
import { faBuilding } from '@fortawesome/pro-solid-svg-icons/faBuilding';
import { faBaby } from '@fortawesome/pro-solid-svg-icons/faBaby';
import { faShoePrints } from '@fortawesome/pro-solid-svg-icons/faShoePrints';
import { faBackpack } from '@fortawesome/pro-solid-svg-icons/faBackpack';
import { faMagnifyingGlassPlus } from '@fortawesome/pro-solid-svg-icons/faMagnifyingGlassPlus';
import { faDog } from '@fortawesome/pro-solid-svg-icons/faDog';
import { faCat } from '@fortawesome/pro-solid-svg-icons/faCat';
import { faPaw } from '@fortawesome/pro-solid-svg-icons/faPaw';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import {
  DateFormat,
  formatDateFromString,
  getShortDateString,
  isBeforeNow,
} from 'us-react-components/dist/utils/date';
import { Button, ButtonType } from '../../../components/Button';
import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { AxiosError, HeicConvertService, Sitter } from 'us-web-services';
import { iconUrls } from '../../../constants/IconUrls';
import { useDependencies } from '../../../dependencies';
import { ModalName } from '../../../modals/constants';
import { useModal } from '../../../modals/modalContext';

const MAX_ABOUT = 500;

export interface SitterRelationshipExtras {
  bookmarked?: boolean;
  blocked?: boolean;
  blockedId?: number | null;
  reviewed?: boolean;
  bookingCount?: number;
  lastMessaged?: string;
  positiveReview?: boolean;
  parentNote?: { comment?: string; updated?: string };
  previousBookingRates?: boolean;
}

export interface SitterExtras extends Omit<Sitter,
  | 'userStats'
  | 'relationship'
  | 'availabilityStatus'
  | 'specialSituations'
  | 'specialExperiences'
  | 'specialServices'
  | 'teachingSkills'
  | 'certifications'
  | 'rates'
  | 'languages'
  | 'experiences'
> {
  about?: string;
  english?: string;
  relationship?: SitterRelationshipExtras;
  languages?: Array<{ id: number; name?: string }>;
  specialExperiences?: Array<{ id: number; type?: string; name?: string }>;
  specialServices?: Array<{ id: number; type?: string; name?: string }>;
  specialSituations?: Array<{ id: number; type?: string; name?: string }>;
  teachingSkills?: Array<{ id: number; name?: string }>;
  certifications?: Array<{ id: number; name?: string }>;
  college?: {
    college?: { name?: string; url?: string };
    graduation?: string;
    areaOfStudy?: { name?: string };
  };
  userStats?: { lastAccessLabel?: string; bookingsCount?: number };
  badges?: Array<{ type?: string; label?: string; description?: string; url?: string; icon?: string }>;
  worksWithin?: number;
  willSitDog?: boolean;
  willSitCat?: boolean;
  willSitOtherPet?: boolean;
  willSitSenior?: boolean;
  willSitHome?: boolean;
  petSittingExperience?: string;
  seniorSittingExperience?: string;
  homeSittingExperience?: string;
  vimeoId?: string;
  rates?: Array<{ minimumRate?: number; maximumRate?: number }>;
  maxKids?: number;
  minimumHours?: number;
  acceptsFixedRates?: boolean;
  willSitSingleFamily?: boolean;
  availabilityStatus?: {
    jobPreferences?: Array<{ id: number; type?: string; code?: string }>;
    description?: string;
    modified?: string;
    recurringAvailability?: Array<unknown>;
  };
  repeatFamilies?: number;
  reviewCount?: number;
  responseTime?: number;
  rating?: number;
  phone?: string;
  hiddenReason?: number;
  experiences?: {
    infant?: { id?: number; label?: string };
    toddler?: { id?: number; label?: string };
    school?: { id?: number; label?: string };
  };
  reliabilityPolicy?: string;
  acceptsNewFamilies?: boolean;
  expressedInterestInMyJobs?: boolean;
}

const formatGraduationDate = (iso?: string): string =>
  iso ? formatDateFromString(iso, DateFormat.DATE_MONTH_YEAR) : '';

const hasGraduated = (iso?: string): boolean => isBeforeNow(iso);

// -- NotAcceptingFamiliesBanner --------------------------------------------
// Angular source: sitter-profile-header.tpl.html — shows when the sitter is
// not currently accepting new families. Matches Angular's `acceptingNewFamilies()`:
// the banner is suppressed when *any* of (acceptsNewFamilies, prior booking
// relationship, expressed interest in viewer's jobs) is true.
export const NotAcceptingFamiliesBanner = ({
  sitter,
}: {
  sitter: SitterExtras;
}): JSX.Element | null => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const [dismissed, setDismissed] = useState(false);
  const accepting =
    !!sitter.acceptsNewFamilies ||
    !!sitter.relationship?.previousBookingRates ||
    !!sitter.expressedInterestInMyJobs;
  if (accepting || dismissed) return null;
  return (
    <div
      role='alert'
      className='relative mb-4 rounded border border-yellow-300 bg-yellow-50 text-yellow-900 px-4 py-3 pr-10'
    >
      <Button
        buttonType={ButtonType.tertiary}
        aria-label={t('pages.sitterProfile.subcomponents.dismissAriaLabel')}
        onClick={(): void => setDismissed(true)}
        className='absolute right-2 top-2 text-yellow-900 hover:text-yellow-950 bg-transparent border-0 p-1'
      >
        &times;
      </Button>
      <strong>Sorry, I&apos;m not currently accepting new families.</strong>
    </div>
  );
};

// -- SitterSnippet ---------------------------------------------------------
// Self-view photo upload. Mirrors apps/react_caregiver/.../SitterRegistration/Photo.tsx:
// pick file → optionally HEIC-convert → open Cropper modal that POSTs the cropped
// blob to UserPictureService. Replaces the legacy <a> link that bounced the
// caregiver to /app/sitter/registration/photo.
const isProbablyHeic = (file: File): boolean =>
  file.type === 'image/heic' ||
  file.type === 'image/heif' ||
  /\.(heic|heif)$/i.test(file.name);

const PhotoEditButton = ({
  className,
  ariaLabel,
  children,
}: {
  className: string;
  ariaLabel: string;
  children: React.ReactNode;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const { stores } = useDependencies();
  const modal = useModal();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const openCropper = (objectUrl: string): void => {
    stores.user.setTemporaryPhoto(objectUrl);
    modal?.setModalName(ModalName.Cropper);
  };

  const handleUpload = async (file: File): Promise<void> => {
    if (!isProbablyHeic(file)) {
      openCropper(URL.createObjectURL(file));
      return;
    }
    let buffer: ArrayBuffer;
    try {
      buffer = await file.arrayBuffer();
    } catch {
      toast.error(t('pages.sitterPhoto.uploadFailed'));
      return;
    }
    if (!HeicConvertService.isHeic(new Uint8Array(buffer))) {
      openCropper(URL.createObjectURL(file));
      return;
    }
    try {
      const postData = new FormData();
      postData.append('file', new Blob([buffer]));
      const response = (await HeicConvertService.convert(postData)).data.data;
      const base64 = btoa(
        response.data.reduce(
          (res: string, byte: number) => res + String.fromCharCode(byte),
          '',
        ),
      );
      openCropper(`data:image/png;base64,${base64}`);
    } catch (e) {
      if (e instanceof AxiosError && e.response?.data?.error) {
        toast.error(e.response.data.error);
      } else {
        toast.error(t('pages.sitterPhoto.uploadFailed'));
      }
    }
  };

  const onFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
  };

  return (
    <>
      <Button
        buttonType={ButtonType.tertiary}
        onClick={(): void => fileInputRef.current?.click()}
        aria-label={ariaLabel}
        title={ariaLabel}
        className={`${className} cursor-pointer !border-0 !p-0`}
      >
        {children}
      </Button>
      <input
        id='sitter-profile-photo-input'
        ref={fileInputRef}
        type='file'
        accept='image/*, .heic'
        className='hidden'
        onChange={onFileChange}
        onClick={(e): void => {
          // Reset value so selecting the same file re-triggers onChange.
          (e.target as HTMLInputElement).value = '';
        }}
      />
    </>
  );
};

export const SitterSnippet = ({
  sitter,
  isSelf,
  isMember,
  isSitter,
  rateText,
  rateLabel,
  distanceText,
  onPlayVideo,
  onFavoriteToggle,
}: {
  sitter: SitterExtras;
  isSelf: boolean;
  isMember: boolean;
  isSitter: boolean;
  rateText: string;
  rateLabel: string;
  distanceText: string;
  onPlayVideo: () => void;
  onFavoriteToggle: () => void;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const blocked = !!sitter.relationship?.blocked;
  const bookmarked = !!sitter.relationship?.bookmarked;
  const showFavorite = isMember && !isSitter && !blocked;
  const showVideoPlay = !isSelf && sitter.vimeoId && !blocked;

  return (
    <div className='flex flex-col sm:flex-row gap-4 mb-5'>
      <div className='profile-page-pic relative flex-shrink-0 w-48 h-48'>
        <img
          alt={t('pages.sitterProfile.subcomponents.profileAlt')}
          src={sitter.pictureUrl}
          className='w-48 h-48 rounded-lg object-cover'
        />
        {blocked && (
          <span
            id={`blocked-overlay-${sitter.id}`}
            className='absolute inset-0 bg-white/70 flex items-center justify-center rounded-lg'
          >
            <FontAwesomeIcon icon={faEyeSlash} className='text-neutral-500 text-5xl' />
          </span>
        )}
        {showVideoPlay && (
          <Button
            buttonType={ButtonType.tertiary}
            id={`play-video-${sitter.id}`}
            onClick={onPlayVideo}
            className='absolute bottom-2 left-2 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center'
            aria-label={t('pages.sitterProfile.subcomponents.playVideoAriaLabel')}
          >
            <FontAwesomeIcon icon={faPlay} className='text-sapphire text-xl' />
          </Button>
        )}
        {showFavorite && (
          <Button
            id={`favorite-${sitter.id}`}
            buttonType={ButtonType.tertiary}
            aria-label={bookmarked ? 'Remove from favorites' : 'Add to favorites'}
            onClick={onFavoriteToggle}
            className={`absolute bottom-2 right-2 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center ${
              bookmarked ? 'text-bubblegum' : 'text-neutral-500'
            }`}
          >
            <FontAwesomeIcon icon={bookmarked ? faHeart : faHeartRegular} className='text-xl' />
          </Button>
        )}
        {isSelf && (
          <PhotoEditButton
            ariaLabel='Change photo'
            className='absolute bottom-2 right-2 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center text-sapphire hover:bg-neutral-50'
          >
            <FontAwesomeIcon icon={faPenToSquare} />
          </PhotoEditButton>
        )}
      </div>
      <div className='profile-summary-text-container flex-1'>
        <div className='font-medium mb-4'>
          <div className='profile-city'>
            <span className='font-semibold'>{sitter.address?.city}</span>
            {sitter.address?.neighborhood && (
              <span> - {sitter.address.neighborhood}</span>
            )}
            {distanceText && <span> ({distanceText})</span>}
          </div>
          <span id='sitter-rate' className='font-semibold'>
            {rateText}
          </span>
          {rateLabel}
        </div>
        {isSelf && (
          <div className='flex flex-wrap items-center gap-2'>
            {sitter.vimeoId && (
              <Button
                buttonType={ButtonType.tertiary}
                onClick={onPlayVideo}
                className='cursor-pointer bg-transparent border-0 p-0'
              >
                <i className='fa-solid fa-play mr-1' />
                {t('pages.sitterProfile.subcomponents.videoProfile')}
              </Button>
            )}
            <a
              id='video-profile'
              href='/app/sitter/video'
              target='_self'
              className='btn btn-tertiary'
            >
              {sitter.vimeoId ? 'Change video' : (
                <span id='add-new-video'>{t('pages.sitterProfile.subcomponents.addNewVideo')}</span>
              )}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

// -- SitterAbout -----------------------------------------------------------
export const SitterAbout = ({
  sitter,
  canEdit,
  onOpenEditAbout,
  onOpenEditLanguages,
}: {
  sitter: SitterExtras;
  canEdit: boolean;
  onOpenEditAbout: () => void;
  onOpenEditLanguages: () => void;
}): JSX.Element => {
  const [showFull, setShowFull] = useState(false);
  const about = sitter.about ?? '';
  const tooLong = about.length > MAX_ABOUT;
  const visibleText = tooLong && !showFull ? `${about.slice(0, MAX_ABOUT)}…` : about;
  const { t } = useSharedTranslation(namespaces.caregiver);

  return (
    <div id='sitter-about' className='mb-8'>
      {!about && (
        <div className='mb-3'>
          {canEdit ? (
            <Button
              buttonType={ButtonType.tertiary}
              id='sitter-introduce'
              onClick={onOpenEditAbout}
              className='font-semibold cursor-pointer bg-transparent border-0 p-0'
            >
              {t('pages.sitterProfile.subcomponents.introduceYourself')}
            </Button>
          ) : (
            <span>{t('pages.sitterProfile.subcomponents.noBio')}</span>
          )}
        </div>
      )}

      <div className='whitespace-pre-line break-words'>
        {visibleText}
        {tooLong && (
          <Button
            buttonType={ButtonType.tertiary}
            id='toggle-about'
            className='font-semibold ml-1 cursor-pointer bg-transparent border-0 p-0'
            onClick={(): void => setShowFull((s) => !s)}
          >
            {showFull ? 'Less' : 'More'}
          </Button>
        )}
      </div>

      <div className='mt-3'>
        {sitter.english && (
          <p>
            <b>{t('pages.sitterProfile.labels.englishColon')}</b> {sitter.english}
          </p>
        )}
        {(canEdit || (sitter.languages ?? []).length > 0) && (
          <p>
            <b>{t('pages.sitterProfile.labels.languagesColon')}</b>
            {(sitter.languages ?? []).map((l, i, a) => (
              <span key={l.id}> {l.name}{i < a.length - 1 ? ', ' : ''}</span>
            ))}
            {canEdit && (
              <Button
                buttonType={ButtonType.tertiary}
                onClick={onOpenEditLanguages}
                className='ml-2 cursor-pointer text-sapphire bg-transparent border-0 p-0'
                aria-label={t('pages.sitterProfile.modals.editLanguages')}
                title={t('pages.sitterProfile.modals.editLanguages')}
              >
                <FontAwesomeIcon icon={faPenToSquare} />
              </Button>
            )}
          </p>
        )}
      </div>
    </div>
  );
};

// -- SitterOtherServices ---------------------------------------------------
export const SitterOtherServices = ({
  sitter,
  canEdit,
}: {
  sitter: SitterExtras;
  canEdit: boolean;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  const doesPetcare = !!(sitter.willSitDog || sitter.willSitCat || sitter.willSitOtherPet);
  const isNull =
    sitter.willSitDog === undefined &&
    sitter.willSitCat === undefined &&
    sitter.willSitOtherPet === undefined &&
    sitter.willSitSenior === undefined &&
    sitter.willSitHome === undefined;

  const row = (active: boolean, label: string): JSX.Element => (
    <div className={active ? '' : 'text-neutral-400'}>
      {active ? (
        <FontAwesomeIcon icon={faCheck} className='mr-2 text-emerald-600' />
      ) : (
        <span className='text-2xl mr-2'>×</span>
      )}
      {label}
    </div>
  );

  return (
    <div id='sitter-other-services' className='mb-8'>
      <h4 className='font-semibold mb-2'>{t('pages.sitterProfile.labels.otherServicesHeading')}</h4>
      {isNull ? (
        <div className='sitter-other-services-view mt-3'>
          {t('pages.sitterProfile.labels.otherServicesEmpty')}
        </div>
      ) : (
        <div className='sitter-other-services-view mt-3 space-y-1'>
          {row(doesPetcare, t('pages.sitterProfile.labels.petCare'))}
          {row(!!sitter.willSitHome, t('pages.sitterProfile.labels.householdServices'))}
          {row(!!sitter.willSitSenior, t('pages.sitterProfile.labels.seniorCare'))}
        </div>
      )}
      {!canEdit && (
        <div className='mt-3'>
          <Button
            buttonType={ButtonType.tertiary}
            id='edit-other-services-button'
            onClick={(): void => setLearnMoreOpen(true)}
            className='text-sapphire font-semibold cursor-pointer bg-transparent border-0 p-0 inline-flex items-center gap-1'
          >
            <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
            {t('pages.sitterProfile.subcomponents.learnMore')}
          </Button>
        </div>
      )}
      <OtherServicesDetailsModal
        isOpen={learnMoreOpen}
        onClose={(): void => setLearnMoreOpen(false)}
        sitter={sitter}
      />
    </div>
  );
};

const OtherServicesDetailsModal = ({
  isOpen,
  onClose,
  sitter,
}: {
  isOpen: boolean;
  onClose: () => void;
  sitter: SitterExtras;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const petcareUndefined =
    sitter.willSitDog === undefined &&
    sitter.willSitCat === undefined &&
    sitter.willSitOtherPet === undefined;
  const hasPetcare = !!(sitter.willSitDog || sitter.willSitCat || sitter.willSitOtherPet);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.sitterProfile.subcomponents.otherServicesTitle')}
      className='w-full max-w-2xl'
      footer={
        <Button buttonType={ButtonType.tertiary} onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      <section className='mb-6'>
        <h4 className='text-lg font-semibold mb-2'>Pet Care</h4>
        {petcareUndefined && (
          <p className='italic'>Willingness to provide pet care not specified.</p>
        )}
        {!petcareUndefined && !hasPetcare && (
          <p className='italic'>Not willing to provide pet care.</p>
        )}
        {hasPetcare && (
          <div>
            <p className='italic mb-2'>
              Willing to provide pet care for the following animals:
            </p>
            <div className='flex items-start gap-6 mb-3'>
              {sitter.willSitDog && (
                <div className='text-center'>
                  <div className='text-sapphire text-3xl mb-1'>
                    <FontAwesomeIcon icon={faDog} />
                  </div>
                  <div className='text-sm'>Dogs</div>
                </div>
              )}
              {sitter.willSitCat && (
                <div className='text-center'>
                  <div className='text-sapphire text-3xl mb-1'>
                    <FontAwesomeIcon icon={faCat} />
                  </div>
                  <div className='text-sm'>Cats</div>
                </div>
              )}
              {sitter.willSitOtherPet && (
                <div className='text-center'>
                  <div className='text-sapphire text-3xl mb-1'>
                    <FontAwesomeIcon icon={faPaw} />
                  </div>
                  <div className='text-sm'>Other pets</div>
                </div>
              )}
            </div>
            {sitter.petSittingExperience && <p>{sitter.petSittingExperience}</p>}
          </div>
        )}
      </section>

      <section className='mb-6'>
        <h4 className='text-lg font-semibold mb-2'>Senior Care</h4>
        {sitter.willSitSenior === undefined && (
          <p className='italic'>Willingness to provide senior care not specified.</p>
        )}
        {sitter.willSitSenior === false && (
          <p className='italic'>Not willing to provide senior care.</p>
        )}
        {sitter.willSitSenior && (
          <>
            <p className='italic'>Willing to provide non-medical senior care.</p>
            {sitter.seniorSittingExperience && <p>{sitter.seniorSittingExperience}</p>}
          </>
        )}
      </section>

      <section className='mb-6'>
        <h4 className='text-lg font-semibold mb-2'>Household Services</h4>
        {sitter.willSitHome === undefined && (
          <p className='italic'>Willingness to provide household services not specified.</p>
        )}
        {sitter.willSitHome === false && (
          <p className='italic'>Not willing to provide household services.</p>
        )}
        {sitter.willSitHome && (
          <>
            <p className='italic'>
              Willing to provide household services that don&apos;t involve children,
              pets, or senior&nbsp;care.
            </p>
            {sitter.homeSittingExperience && <p>{sitter.homeSittingExperience}</p>}
          </>
        )}
      </section>

    </Modal>
  );
};

const getBadgeIcon = (badge: {
  type?: string;
  icon?: string;
}): IconDefinition | null => {
  if (badge.type === 'event') {
    if (badge.icon?.includes('badge-cpr')) return faHeartPulse;
    if (badge.icon?.includes('badge-trusted-by')) return faHandshake;
    return null;
  }
  switch (badge.type) {
    case 'acceptsCreditCards':
    case 'cc':
      return faCreditCard;
    case 'backgroundCheck':
      return faShieldCheck;
    case 'corporate':
      return faBuilding;
    case 'covidBadge':
      return faSyringe;
    case 'cpr':
      return faHeartPulse;
    case 'family':
    case 'repeatFamilies':
    case 'repeated_sitter':
      return faUsers;
    case 'friend':
      return faUsers;
    case 'member-since':
    case 'sitterMember':
      return null;
    case 'messageResponse':
      return faCommentDots;
    case 'reliable':
      return faCircleCheck;
    case 'review':
    case 'reviewCount':
      return faStar;
    case 'response':
    case 'responseTime':
      return faClock;
    case 'trustedBy':
      return faHandshake;
    default:
      return null;
  }
};

// -- SitterBadges ----------------------------------------------------------
// Angular composes the list from several sitter fields (cancellationRate,
// respondsQuicklyLabel, backgroundCheck/Date) plus the api-supplied
// `sitter.badges`. Mirror that here so the low-cancellation ("reliable")
// badge and the "responds quickly" badge actually show up.
const composeBadges = (
  sitter: SitterExtras,
): Array<{ type?: string; label?: string; description?: string; url?: string; icon?: string }> => {
  const list: Array<{
    type?: string;
    label?: string;
    description?: string;
    url?: string;
    icon?: string;
  }> = [];

  const bc = sitter as unknown as {
    backgroundCheck?: boolean;
    backgroundCheckDate?: string;
    cancellationRate?: { label?: string };
    respondsQuicklyLabel?: string;
  };

  if (bc.backgroundCheck && bc.backgroundCheckDate) {
    const d = new Date(bc.backgroundCheckDate);
    const formatted = Number.isNaN(d.getTime())
      ? bc.backgroundCheckDate
      : d.toLocaleDateString('en-US');
    list.push({
      type: 'backgroundCheck',
      description: `Background check completed on ${formatted}`,
      label: 'Background check',
    });
  }
  if (bc.cancellationRate?.label) {
    list.push({ type: 'reliable', label: bc.cancellationRate.label });
  }
  if (bc.respondsQuicklyLabel) {
    list.push({ type: 'messageResponse', label: bc.respondsQuicklyLabel });
  }
  (sitter.badges ?? []).forEach((badge) => {
    if (badge.type && badge.type !== 'backgroundCheck' && badge.type !== 'covidBadge') {
      list.push(badge);
    }
  });
  return list;
};

export const SitterBadges = ({ sitter }: { sitter: SitterExtras }): JSX.Element => {
  const badges = composeBadges(sitter);
  return (
    <div>
      {badges.map((badge, index) => {
        if (!badge.type) return null;
        const icon = getBadgeIcon(badge);
        const onClick = (): void => {
          if (badge.url) window.open(badge.url, '_blank');
        };
        return (
          <div key={`${badge.type}-${index}`} className='mb-3 flex items-center gap-2'>
            <span
              id={`${badge.type}-${index}`}
              title={badge.description ?? badge.label}
              onClick={onClick}
              onKeyDown={(e): void => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick();
                }
              }}
              role={badge.url ? 'button' : undefined}
              tabIndex={badge.url ? 0 : undefined}
              className={`w-8 h-8 rounded-full bg-sapphire-50 text-sapphire flex items-center justify-center flex-shrink-0 ${
                badge.url ? 'cursor-pointer' : ''
              }`}
            >
              {icon ? (
                <FontAwesomeIcon icon={icon} />
              ) : (
                <img
                  src={iconUrls.brandBadge}
                  alt=''
                  className='w-6 h-6'
                />
              )}
            </span>
            <span className='about-me-badge-text'>{badge.label}</span>
          </div>
        );
      })}
    </div>
  );
};

// -- SitterAgeExperience ---------------------------------------------------
export const SitterAgeExperience = ({ sitter }: { sitter: SitterExtras }): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const getLabel = (ageLabel: string, exp?: { label?: string }): string => {
    if (!exp?.label) return '';
    const suffix = exp.label.includes('Not willing') ? ` ${ageLabel}` : '';
    return `${exp.label}${suffix}`;
  };

  const hasExperience = (exp?: { label?: string }): boolean =>
    !!exp?.label && !exp.label.includes('Not willing');

  const row = (
    icon: IconDefinition,
    iconKey: string,
    bold: string,
    range: string,
    exp?: { label?: string },
  ): JSX.Element => {
    const active = hasExperience(exp);
    return (
      <div className='flex items-start gap-3 mb-3'>
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
            active ? 'bg-sapphire-100 text-sapphire' : 'bg-neutral-100 text-neutral-400'
          }`}
          title={iconKey}
        >
          <FontAwesomeIcon icon={icon} />
        </div>
        <div>
          <b>{bold}</b> {range}
          <br />
          <b>{getLabel(iconKey, exp)}</b>
        </div>
      </div>
    );
  };

  const exp = sitter.experiences ?? {};

  return (
    <div>
      <h4 className='font-semibold mb-3'>{t('pages.sitterProfile.labels.childrensAgesHeading')}</h4>
      <div className='center-left'>
        {row(faBaby, 'infants', t('pages.sitterProfile.labels.infants'), '(under 1 year old)', exp.infant)}
        {row(faShoePrints, 'toddlers', t('pages.sitterProfile.labels.toddlers'), '(1-3 years old)', exp.toddler)}
        {row(faBackpack, 'school age children', t('pages.sitterProfile.labels.schoolAge'), '(4+ years old)', exp.school)}
      </div>
    </div>
  );
};

// -- SitterRates -----------------------------------------------------------
export const SitterRates = ({
  sitter,
  parent,
}: {
  sitter: SitterExtras;
  parent?: { signupVerticalTypes?: Array<{ signupVerticalType: { id: number } }> };
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const verticalIds = {
    CHILD_CARE: 1,
    PET_CARE_DOG: 2,
    PET_CARE_CAT: 3,
    PET_CARE_OTHER: 4,
    SENIOR_CARE: 5,
    HOUSEHOLD_SERVICES: 6,
  };
  const petcareIds = [verticalIds.PET_CARE_DOG, verticalIds.PET_CARE_CAT, verticalIds.PET_CARE_OTHER];

  const signupHas = (id: number): boolean =>
    !!parent?.signupVerticalTypes?.some((v) => v.signupVerticalType.id === id);
  const signupHasAny = (ids: number[]): boolean =>
    !!parent?.signupVerticalTypes?.some((v) => ids.includes(v.signupVerticalType.id));

  const wantsChildcare = signupHas(verticalIds.CHILD_CARE);
  const wantsPetcare = signupHasAny(petcareIds);
  const wantsSeniorcare = signupHas(verticalIds.SENIOR_CARE);
  const wantsHousehold = signupHas(verticalIds.HOUSEHOLD_SERVICES);
  const onlyHousehold = wantsHousehold && !wantsChildcare && !wantsPetcare && !wantsSeniorcare;

  const getRateTypeText = (count: number): string => {
    if (wantsChildcare) return count === 1 ? 'child' : 'children';
    if (wantsSeniorcare) return count === 1 ? 'senior' : 'seniors';
    if (wantsPetcare) return count === 1 ? 'pet' : 'pets';
    return count === 1 ? 'child, senior, or pet' : 'children, seniors, or pets';
  };

  const getRateTypeLabel = (count: number): string => {
    if (onlyHousehold && count === 1) return '';
    return ` for ${count} ${getRateTypeText(count)}`;
  };

  const rates = sitter.rates ?? [];

  const lines: string[] = [];
  for (let i = 0; i < 4; i += 1) {
    const rate = rates[i];
    if (!rate || !rate.minimumRate || rate.minimumRate <= 0) continue;
    if (onlyHousehold && i === 0) {
      lines.push(`$${rate.minimumRate} per hour`);
    } else if (!onlyHousehold) {
      const max =
        rate.maximumRate && rate.maximumRate !== rate.minimumRate
          ? `-${rate.maximumRate}`
          : '';
      lines.push(`$${rate.minimumRate}${max} per hour${getRateTypeLabel(i + 1)}`);
    }
  }

  const maxText = sitter.maxKids
    ? `${sitter.maxKids} ${getRateTypeText(sitter.maxKids)} maximum`
    : '';
  const minText = sitter.minimumHours
    ? `${sitter.minimumHours} hour minimum per booking`
    : '';

  return (
    <div>
      <h4 id='sitter-rates' className='font-semibold mb-2'>
        {t('pages.sitterProfile.labels.ratesHeading')}
      </h4>
      {lines.length > 0 ? (
        lines.map((line, i) => (
          <span key={i} className='line-height-33 block'>
            {line}
          </span>
        ))
      ) : (
        <div className='text-neutral-500'>{t('pages.sitterProfile.labels.noInfo')}</div>
      )}
      <br />
      {!onlyHousehold && maxText && <span className='mt-1 block'>{maxText}</span>}
      {minText && <span className='mt-1 block'>{minText}</span>}
      <br />
      {sitter.acceptsFixedRates && (
        <div>{t('pages.sitterProfile.labels.openToFlatRates')}</div>
      )}
      {sitter.willSitSingleFamily && (
        <div className='mb-4'>
          {t('pages.sitterProfile.labels.willingExclusive')}
        </div>
      )}
    </div>
  );
};

// -- Checklist-style lists -------------------------------------------------
const Checklist = ({
  heading,
  items,
  emptyText,
  idPrefix,
  itemKey,
}: {
  heading: string;
  items: Array<{ id: number; name?: string; type?: string }>;
  emptyText: string;
  idPrefix?: string;
  itemKey?: 'type' | 'id';
}): JSX.Element => (
  <div>
    <h4 className='font-semibold mb-2'>{heading}</h4>
    {items.length === 0 && <div className='text-neutral-500'>{emptyText}</div>}
    {items.length > 0 && (
      <ul className='list-none mb-8'>
        {items.map((item) => {
          const k = itemKey === 'type' ? item.type : item.id;
          return (
            <li
              key={item.id}
              id={idPrefix ? `${idPrefix}-${k}` : undefined}
              className='flex items-center gap-2 mb-1'
            >
              <FontAwesomeIcon icon={faCheck} className='text-emerald-600' />
              {item.name}
            </li>
          );
        })}
      </ul>
    )}
  </div>
);

export const SitterTeachingSkills = ({ sitter }: { sitter: SitterExtras }): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  return (
    <Checklist
      heading={t('pages.sitterProfile.labels.teachingSkillsHeading')}
      items={sitter.teachingSkills ?? []}
      emptyText={t('pages.sitterProfile.labels.noInfo')}
      idPrefix='display-teaching-skill'
      itemKey='id'
    />
  );
};

export const SitterSpecialExperience = ({
  sitter,
}: {
  sitter: SitterExtras;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  return (
    <Checklist
      heading={t('pages.sitterProfile.labels.specialExperienceHeading')}
      items={sitter.specialExperiences ?? []}
      emptyText={t('pages.sitterProfile.labels.noInfo')}
      idPrefix='special-experience'
      itemKey='type'
    />
  );
};

export const SitterSpecialServices = ({ sitter }: { sitter: SitterExtras }): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  return (
    <Checklist
      heading={t('pages.sitterProfile.labels.willingToHelpHeading')}
      items={sitter.specialServices ?? []}
      emptyText={t('pages.sitterProfile.labels.noSpecificDuties')}
      idPrefix='special-services'
      itemKey='type'
    />
  );
};

export const SitterSpecialSituations = ({
  sitter,
}: {
  sitter: SitterExtras;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  return (
    <Checklist
      heading={t('pages.sitterProfile.labels.specialSituationsHeading')}
      items={sitter.specialSituations ?? []}
      emptyText={t('pages.sitterProfile.labels.noSituations')}
      idPrefix='special-situations'
      itemKey='type'
    />
  );
};

// -- SitterEducation -------------------------------------------------------
export const SitterEducation = ({ sitter }: { sitter: SitterExtras }): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const college = sitter.college;
  if (!college?.college) {
    return (
      <div>
        <h4 className='font-semibold mb-2'>{t('pages.sitterProfile.labels.educationHeading')}</h4>
        <p>{t('pages.sitterProfile.labels.noInfo')}</p>
      </div>
    );
  }

  const graduated = hasGraduated(college.graduation);
  const name = college.college.name ?? '';
  const url = college.college.url;

  return (
    <div>
      <h4 className='font-semibold mb-2'>{t('pages.sitterProfile.labels.educationHeading')}</h4>
      <p>
        <b>
          {!college.graduation
            ? t('pages.sitterProfile.labels.collegeColon')
            : graduated
              ? t('pages.sitterProfile.labels.graduatedFromColon')
              : t('pages.sitterProfile.labels.currentlyAttendingColon')}
        </b>{' '}
        {url ? (
          <a href={url} target='_blank' rel='noreferrer' className='underline'>
            {name}
          </a>
        ) : (
          name
        )}
      </p>
      {college.areaOfStudy?.name && (
        <p>
          <b>{t('pages.sitterProfile.labels.areaOfStudyColon')}</b> {college.areaOfStudy.name}
        </p>
      )}
      {college.graduation && (
        <p>
          <b>{graduated ? t('pages.sitterProfile.labels.graduationDateColon') : t('pages.sitterProfile.labels.expectedGraduationDateColon')}</b>{' '}
          {formatGraduationDate(college.graduation)}
        </p>
      )}
    </div>
  );
};

// -- SitterCertifications --------------------------------------------------
export const SitterCertifications = ({
  sitter,
}: {
  sitter: SitterExtras;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const certs = sitter.certifications ?? [];
  return (
    <div>
      <h4 className='font-semibold mb-2'>{t('pages.sitterProfile.labels.certificationsHeading')}</h4>
      {certs.length === 0 && <div className='text-neutral-500'>{t('pages.sitterProfile.labels.noInfo')}</div>}
      {certs.length > 0 && (
        <ul className='list-none mb-8'>
          {certs.map((c) => (
            <li
              key={c.id}
              id={`certification-${c.name}`}
              className='flex items-center gap-2 mb-1'
            >
              <FontAwesomeIcon icon={faCheck} className='text-emerald-600' />
              {c.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// -- SitterLocation --------------------------------------------------------
export const SitterLocation = ({
  sitter,
  isSelf,
  onEditLocation,
}: {
  sitter: SitterExtras;
  isSelf: boolean;
  onEditLocation: () => void;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  return (
  <div>
    {sitter.address?.city && (
      <p>
        <span className='font-semibold mb-1'>{t('pages.sitterProfile.labels.livesInColon')}</span> {sitter.address.city}
      </p>
    )}
    <p>
      <span className='font-semibold mb-1'>{t('pages.sitterProfile.labels.neighborhoodColon')}</span>{' '}
      {sitter.address?.neighborhood}
      {isSelf && !sitter.address?.neighborhood && (
        <Button
          buttonType={ButtonType.tertiary}
          id='add-neighborhood'
          className='font-semibold cursor-pointer ml-1 underline bg-transparent border-0 p-0'
          onClick={onEditLocation}
        >
          {t('pages.sitterProfile.labels.addNeighborhood')}
        </Button>
      )}
    </p>
    {sitter.worksWithin && sitter.worksWithin > 0 && (
      <p>
        <span className='font-semibold mb-1'>{t('pages.sitterProfile.labels.willingToWorkWithinColon')}</span>{' '}
        {sitter.worksWithin} miles
      </p>
    )}
    {isSelf && (
      <div className='text-right'>
        <Button
          buttonType={ButtonType.tertiary}
          id='edit-locations'
          onClick={onEditLocation}
          className='cursor-pointer text-sapphire bg-transparent border-0 p-0'
          aria-label={t('pages.sitterProfile.modals.editLocation')}
          title={t('pages.sitterProfile.modals.editLocation')}
        >
          <FontAwesomeIcon icon={faPenToSquare} />
        </Button>
      </div>
    )}
  </div>
  );
};

// -- AvailabilityStatus (wrapper + occasional + recurring snapshot) --------
// The API stores JobAvailabilityType as the enum's string value
// ('Occasionally', 'Part Time', 'Full Time') — see services/types/Job.ts.
// Match against the canonical strings, not the lowercase variant.
const isOccasionalSelected = (sitter: SitterExtras): boolean =>
  !!sitter.availabilityStatus?.jobPreferences?.some(
    (j) => j.type === 'Occasionally' || j.code === 'occasional',
  );

const isTypeSelected = (sitter: SitterExtras, value: string): boolean =>
  !!sitter.availabilityStatus?.jobPreferences?.some(
    (j) => j.type === value || j.code === value,
  );

const getRecurringTitle = (sitter: SitterExtras): string => {
  const isPart = isTypeSelected(sitter, 'Part Time');
  const isFull = isTypeSelected(sitter, 'Full Time');
  if (isPart && isFull) return 'Part-time and full-time jobs';
  if (isPart) return 'Part-time jobs';
  if (isFull) return 'Full-time jobs';
  return '';
};

const daysFromNow = (iso?: string): number => {
  if (!iso) return 0;
  const d = new Date(iso).getTime();
  return Math.round((Date.now() - d) / 86400000);
};

const getExpirationLabel = (modified?: string): string => {
  if (!modified) return '';
  const since = daysFromNow(modified);
  const expirationDays = since - 30;
  if (expirationDays > 0) return 'expired!';
  if (expirationDays === 0) return 'expires today!';
  if (expirationDays === -1) return 'expires tomorrow';
  return `expires in ${-expirationDays} days`;
};

interface RecurringDay {
  dayOfWeek?: { code?: string };
  morning?: boolean;
  afternoon?: boolean;
  night?: boolean;
}

const RecurringSnapshot = ({ days }: { days: RecurringDay[] }): JSX.Element => {
  const cell = (on: boolean, icon: IconDefinition, onClass: string): JSX.Element => (
    <FontAwesomeIcon
      icon={on ? icon : faSquare}
      className={on ? onClass : 'text-neutral-300'}
    />
  );
  return (
    <div className='flex justify-end'>
      <div className='inline-grid grid-cols-[1.25rem_repeat(7,1.5rem)] items-center gap-x-1'>
        <div />
        {days.map((day, i) => (
          <div key={`h-${i}`} className='text-center text-[10px]'>
            {day.dayOfWeek?.code?.charAt(0) ?? ''}
          </div>
        ))}

        <div className='text-[10px]'>AM</div>
        {days.map((day, i) => (
          <div key={`am-${i}`} className='text-center'>
            {cell(!!day.morning, faSun, 'text-yellow-500')}
          </div>
        ))}

        <div className='text-[10px]'>PM</div>
        {days.map((day, i) => (
          <div key={`pm-${i}`} className='text-center'>
            {cell(!!day.afternoon, faSun, 'text-yellow-500')}
          </div>
        ))}

        <div />
        {days.map((day, i) => (
          <div key={`n-${i}`} className='text-center'>
            {cell(!!day.night, faMoon, 'text-sapphire')}
          </div>
        ))}
      </div>
    </div>
  );
};

export const AvailabilityStatusWrapper = ({
  sitter,
  canEdit,
  stats,
  onOpenAvailabilityModal,
}: {
  sitter: SitterExtras;
  canEdit: boolean;
  stats?: { generalAvailability?: { label?: string } };
  onOpenAvailabilityModal: () => void;
}): JSX.Element | null => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const prefs = sitter.availabilityStatus?.jobPreferences ?? [];
  if (!canEdit && prefs.length === 0) return null;

  const occasional = isOccasionalSelected(sitter);
  const recurringTitle = getRecurringTitle(sitter);
  const hasRecurringCalendar = !!(sitter.availabilityStatus?.recurringAvailability ?? []).length;
  const standalone = !occasional;

  return (
    <div
      id='availability-wrapper'
      className='bg-sapphire-50 p-3 mb-5 rounded'
    >
      <h3 className='font-semibold mb-2'>Looking for...</h3>

      {prefs.length > 0 && (
        <>
          {/* Occasional */}
          <div className='mb-2'>
            {!occasional ? (
              <div>{t('pages.sitterProfile.subcomponents.notLookingOccasional')}</div>
            ) : (
              <div className='flex items-start justify-between'>
                <h4 className='font-semibold'>Occasional jobs</h4>
                <div className='flex items-center gap-2'>
                  <FontAwesomeIcon icon={faCalendar} className='text-sapphire text-2xl' />
                  {sitter.id && (
                    <a
                      href={canEdit ? '/app/sitter-calendar' : `/app/sitter/${sitter.id}/calendar`}
                      className='underline text-sm'
                    >
                      {t('pages.sitterProfile.subcomponents.seeOccasionalCalendar')}
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
          <hr className='my-2' />

          {/* Recurring */}
          <div className='mb-2'>
            {recurringTitle ? (
              <div className='grid grid-cols-1 md:grid-cols-12 gap-3'>
                <div className='md:col-span-7'>
                  <h4 className='font-semibold mb-0'>
                    {standalone ? `Looking for ${recurringTitle.toLowerCase()}` : recurringTitle}
                  </h4>
                  {sitter.availabilityStatus?.description && (
                    <p className='mt-1 text-sm'>
                      {sitter.availabilityStatus.description}
                    </p>
                  )}
                  {standalone && sitter.availabilityStatus?.modified && (
                    <p className='mt-1 italic text-sm'>
                      - Last updated{' '}
                      {getShortDateString(sitter.availabilityStatus.modified)}
                    </p>
                  )}
                </div>
                <div className='md:col-span-5'>
                  {standalone && hasRecurringCalendar && (
                    <div className='font-semibold mb-1 text-center'>Generally available</div>
                  )}
                  {(hasRecurringCalendar || canEdit) && (
                    <RecurringSnapshot
                      days={(sitter.availabilityStatus?.recurringAvailability ?? []) as RecurringDay[]}
                    />
                  )}
                </div>
              </div>
            ) : (
              <div>
                {standalone
                  ? 'Looking for occasional jobs'
                  : 'Not looking for full-time or part-time jobs'}
              </div>
            )}
          </div>
        </>
      )}

      <div className='grid grid-cols-1 md:grid-cols-2 gap-2 mt-2'>
        <div>
          {prefs.length === 0 && <span>{t('pages.sitterProfile.subcomponents.notLookingJobs')}</span>}
          {prefs.length > 0 && stats?.generalAvailability?.label && (
            <i className='block'>
              Last updated {stats.generalAvailability.label}
              {canEdit && (
                <span> ({getExpirationLabel(sitter.availabilityStatus?.modified)})</span>
              )}
            </i>
          )}
        </div>
        <div className='text-right'>
          <Button
            buttonType={ButtonType.tertiary}
            onClick={onOpenAvailabilityModal}
            className='cursor-pointer underline text-sm bg-transparent border-0 p-0 inline-flex items-center gap-1'
          >
            {canEdit && <FontAwesomeIcon icon={faPenToSquare} />}
            {canEdit ? 'Update availability' : 'View full availability'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// -- SitterRelationship ----------------------------------------------------
const getSlashesDate = (iso?: string): string =>
  iso ? formatDateFromString(iso, DateFormat.DATE_LONG_FORMAT_SLASHES) : '';

export const SitterRelationship = ({
  sitter,
  userId,
  onOpenNoteModal,
}: {
  sitter: SitterExtras;
  userId?: number;
  onOpenNoteModal: () => void;
}): JSX.Element | null => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const [limited, setLimited] = useState(true);
  const rel = sitter.relationship;
  const hasRelationship = !!(
    rel?.bookingCount ||
    rel?.lastMessaged ||
    rel?.positiveReview ||
    rel?.parentNote
  );
  if (!hasRelationship) return null;

  const parentNote = rel?.parentNote;
  const comment = parentNote?.comment ?? '';

  return (
    <div
      id='relationship-with-sitter'
      className='bg-neutral-100 mb-4 p-4 rounded'
    >
      <h3 className='font-semibold mb-2'>Your relationship</h3>
      <div className='bg-white p-4 rounded'>
        {rel?.bookingCount != null && rel.bookingCount > 0 && (
          <a
            id={`num-bookings-to-list-${sitter.id}`}
            href={`/app/bookings?filterType=all_active&sitterId=${sitter.id}`}
            className='block my-2'
          >
            <FontAwesomeIcon icon={faCircleCheck} className='text-black mr-1' />
            {rel.bookingCount} booking{rel.bookingCount > 1 ? 's' : ''} with you
          </a>
        )}
        {rel?.lastMessaged && (
          <a
            id={`num-messages-to-messages-${sitter.id}`}
            className='mb-2 block'
            href={`/app/parent/${userId}/messages/${sitter.id}`}
          >
            <FontAwesomeIcon icon={faComment} className='text-black mr-1' aria-hidden='true' />
            Last message {getSlashesDate(rel.lastMessaged)}
          </a>
        )}
        {rel?.positiveReview && (
          <a
            id={`positive-review-to-reviews-${sitter.id}`}
            className='mb-2 block'
            href={`/app/sitter/${sitter.id}/reviews`}
          >
            <FontAwesomeIcon icon={faStar} className='text-black mr-1' />
            {t('pages.sitterProfile.subcomponents.positiveReview')}
          </a>
        )}
        {parentNote && (
          <div>
            <i>
              {limited ? comment.slice(0, 300) : comment}
              {comment.length > 300 && (
                <Button
                  buttonType={ButtonType.tertiary}
                  onClick={(): void => setLimited(!limited)}
                  className='cursor-pointer ml-1 underline bg-transparent border-0 p-0'
                >
                  ... view {limited ? 'more' : 'less'}
                </Button>
              )}
            </i>
          </div>
        )}
        {parentNote?.updated && (
          <span className='text-neutral-500 text-sm'>
            <FontAwesomeIcon icon={faLock} className='mr-1' aria-hidden='true' />
            {getSlashesDate(parentNote.updated)}
          </span>
        )}
        <Button
          buttonType={ButtonType.tertiary}
          id='create-or-edit-note'
          onClick={onOpenNoteModal}
          className='cursor-pointer underline ml-2 bg-transparent border-0 p-0'
        >
          {parentNote ? 'Edit' : 'Save private note about caregiver'}
        </Button>
      </div>
    </div>
  );
};

// -- SitterProfileSidePanel ------------------------------------------------
export const SitterProfileSidePanel = ({
  sitter,
  user,
  isParent,
  isSelf,
  canEdit,
  page,
  onBook,
  onMessage,
  onInterview,
  onToggleHidden,
  onOpenFlagModal,
  onReviewClick,
  recommendLink,
}: {
  sitter: SitterExtras;
  user?: { id?: number; parent?: boolean };
  isParent: boolean;
  isSelf: boolean;
  canEdit: boolean;
  page: 'summary' | 'reviews' | 'friends' | 'calendar';
  onBook: () => void;
  onMessage: () => void;
  onInterview: () => void;
  onToggleHidden: () => void;
  onOpenFlagModal: () => void;
  onReviewClick: () => void;
  recommendLink: string;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const reviewCount = sitter.reviewCount ?? 0;
  const repeatFamilies = sitter.repeatFamilies ?? 0;
  const responseTime = sitter.responseTime ?? 0;
  const rating = sitter.rating ?? 0;
  const stars = Math.round(rating);

  return (
    <div className='space-y-3'>
      <div
        className='stars-reviews-bar cursor-pointer text-center mb-3'
        onClick={onReviewClick}
        onKeyDown={(e): void => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onReviewClick();
          }
        }}
        role='button'
        tabIndex={0}
      >
        {rating > 0 && (
          <div className='flex justify-center gap-0.5 mb-1'>
            {[1, 2, 3, 4, 5].map((n) => (
              <FontAwesomeIcon
                key={n}
                icon={faStar}
                className={n <= stars ? 'text-honey' : 'text-gray-100'}
              />
            ))}
          </div>
        )}
        <h4 className='mb-1'>
          {reviewCount > 0 && (
            <span>
              {reviewCount} review{reviewCount === 1 ? '' : 's'}
            </span>
          )}
          {reviewCount > 0 && repeatFamilies > 0 && <span> | </span>}
          {repeatFamilies > 0 && (
            <span>
              {repeatFamilies} repeat famil{repeatFamilies === 1 ? 'y' : 'ies'}
            </span>
          )}
          {responseTime > 0 && (
            <div>
              Responds in {responseTime} hour{responseTime === 1 ? '' : 's'}
            </div>
          )}
        </h4>
      </div>

      {!canEdit && isParent && user?.id && (
        <div className='flex flex-col gap-2'>
          <Button
            id={`booking-${sitter.id}`}
            buttonType={ButtonType.primary}
            className='btn-block w-full'
            onClick={onBook}
          >
            {t('pages.sitterProfile.subcomponents.requestBooking')}
          </Button>
          <Button
            id={`message-${sitter.id}`}
            buttonType={ButtonType.secondary}
            className='w-full'
            onClick={onMessage}
          >
            {t('pages.sitterProfile.subcomponents.message')}
          </Button>
          <Button
            id={`interview-${sitter.id}`}
            buttonType={ButtonType.secondary}
            className='w-full'
            onClick={onInterview}
          >
            {t('pages.sitterProfile.subcomponents.interview')}
          </Button>
          <details className='relative'>
            <summary className='btn btn-secondary w-full cursor-pointer list-none text-center'>
              <FontAwesomeIcon icon={faChevronDown} className='mr-1' />
              {t('pages.sitterProfile.subcomponents.moreOptions')}
            </summary>
            <ul className='absolute right-0 mt-2 bg-white border border-neutral-200 rounded shadow z-10 w-56 py-1'>
              <li>
                <Button
                  id={`toggle-hidden-${sitter.id}`}
                  buttonType={ButtonType.tertiary}
                  onClick={onToggleHidden}
                  className='w-full text-left px-3 py-2 hover:bg-neutral-100'
                >
                  <FontAwesomeIcon
                    icon={sitter.relationship?.blocked ? faEye : faEyeSlash}
                    className='mr-1'
                  />{' '}
                  {sitter.relationship?.blocked ? 'Show' : 'Hide'} caregiver
                </Button>
              </li>
              <li>
                <Button
                  id={`report-sitter-${sitter.id}`}
                  buttonType={ButtonType.tertiary}
                  onClick={onOpenFlagModal}
                  className='w-full text-left px-3 py-2 hover:bg-neutral-100'
                >
                  <FontAwesomeIcon icon={faFlag} className='mr-1' /> Report caregiver
                </Button>
              </li>
            </ul>
          </details>
        </div>
      )}

      {!isParent && isSelf && (
        <div className='my-3 text-center'>
          <h4 className='mb-0 font-semibold'>Get Recommendations</h4>
          Build up your profile by sending the link below to families.
          <div className='mt-2 flex gap-2'>
            <input
              aria-label={t('pages.sitterProfile.subcomponents.shareLinkAriaLabel')}
              type='text'
              readOnly
              value={recommendLink}
              onClick={(e): void => e.currentTarget.select()}
              className='flex-1 border border-neutral-300 rounded px-2 py-1 text-sm'
            />
          </div>
        </div>
      )}

      {/* page is used for conditional side content when needed */}
      {page && null}
    </div>
  );
};
